import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0, 
    }).format(amount);
};

const Profile = () => {
    const { currentUser, logout, authLoading } = useAuth();
    const navigate = useNavigate();
    
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!authLoading && !currentUser) {
            navigate('/login');
        }
    }, [currentUser, authLoading, navigate]);

    useEffect(() => {
        const fetchOrders = async () => {
            if (!currentUser) return;

            try {
                setLoadingOrders(true);
                setError(null);

                const ordersRef = collection(db, 'orders');
                const q = query(
                    ordersRef,
                    where('userId', '==', currentUser.uid),
                    orderBy('orderDate', 'desc')
                );

                const querySnapshot = await getDocs(q);
                const fetchedOrders = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().orderDate?.toDate() || new Date(),
                }));

                setOrders(fetchedOrders);

            } catch (err) {
                console.error("Error fetching user orders:", err);
                setError("Failed to load your order history.");
            } finally {
                setLoadingOrders(false);
            }
        };

        fetchOrders();
    }, [currentUser]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch {
            setError('Failed to log out.');
        }
    };

    if (authLoading || !currentUser) {
        return <div className="text-center p-8">Loading user profile...</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8 border-b pb-4">
                <h1 className="text-3xl font-bold">Welcome, {currentUser.email}</h1>
                <button onClick={handleLogout} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                    Logout
                </button>
            </div>
            
            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-1 bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-bold mb-4">Account Information</h2>
                    <p><strong>Email:</strong> {currentUser.email}</p>
                    <p><strong>User ID:</strong> {currentUser.uid.substring(0, 10)}...</p>
                    <button className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">Update Info</button>
                </div>
                
                <div className="md:col-span-2 bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-bold mb-4">Order History</h2>
                    {loadingOrders ? (
                        <div>Loading orders...</div>
                    ) : orders.length === 0 ? (
                        <div className="text-gray-600">You have not placed any orders yet.</div>
                    ) : (
                        <div className="space-y-4">
                            {orders.map(order => (
                                <div key={order.id} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <p><strong>Order ID:</strong> {order.id.substring(0, 8)}</p>
                                        <p><strong>Date:</strong> {order.createdAt.toLocaleDateString()}</p>
                                        <p><strong>Total:</strong> {formatCurrency(order.orderTotal)}</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {order.items.slice(0, 2).map(item => (
                                            <span key={item.id} className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-sm">
                                                {item.name} (x{item.quantity})
                                            </span>
                                        ))}
                                        {order.items.length > 2 && (
                                            <span className="bg-blue-200 text-blue-700 px-2 py-1 rounded-full text-sm">
                                                +{order.items.length - 2} more items
                                            </span>
                                        )}
                                    </div>
                                    <button className="text-blue-600 hover:underline">View Details</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;